import React from 'react';
import {
  ArrowLeft, Trash2, ShieldCheck, Clock, Mail,
} from 'lucide-react';

/**
 * Shared, static Account Deletion content rendered by the Vike SSR page
 * (src/pages/delete-account/+Page.tsx).
 *
 * Pure JSX — no hooks, no auth, no data fetching — so the full copy is baked
 * into the prerendered HTML and is visible to crawlers (Google Play Data Safety)
 * in view-source without JS. Mirrors the pattern of PrivacyContent.tsx.
 *
 * The retention period (7 years) and legal basis mirror the Privacy Policy
 * (src/components/PrivacyContent.tsx §4.4 and §7) so the two pages cannot
 * contradict each other.
 */
export default function DeleteAccountContent() {
  return (
    <>
      {/* Back-link to the Privacy Policy */}
      <a
        href="/privacy"
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-brand-primary transition-colors mb-8"
      >
        <ArrowLeft size={16} aria-hidden="true" />
        Privacy Policy
      </a>

      {/* Hero */}
      <div className="mb-12">
        <span className="inline-block text-xs font-semibold uppercase tracking-wide text-brand-primary bg-brand-primary/10 rounded-full px-3 py-1 mb-4">
          Account &amp; data
        </span>
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          Delete your Afriqahome account
        </h1>
        <p className="text-gray-600 leading-relaxed text-lg">
          You can permanently delete your Afriqahome account and personal data at any
          time. When you confirm, we sign you out everywhere; after a 30-day grace period
          your data is permanently deleted. This page explains exactly what's removed, what
          we're required to keep and why, and how to make the request.
        </p>
      </div>

      {/* What gets deleted */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Trash2 size={20} className="text-brand-primary flex-shrink-0" aria-hidden="true" />
          What gets deleted — after a 30-day grace period
        </h2>
        <ul className="space-y-3">
          <li className="bg-white border border-brand-border rounded-xl p-4">
            <p className="font-medium text-gray-900">Your profile and login</p>
            <p className="text-sm text-gray-600 mt-1">
              Your account details and the way you sign in to Afriqahome.
            </p>
          </li>
          <li className="bg-white border border-brand-border rounded-xl p-4">
            <p className="font-medium text-gray-900">Your saved properties and searches</p>
            <p className="text-sm text-gray-600 mt-1">
              Your shortlist, saved searches and viewing history.
            </p>
          </li>
          <li className="bg-white border border-brand-border rounded-xl p-4">
            <p className="font-medium text-gray-900">Your messages and conversations</p>
            <p className="text-sm text-gray-600 mt-1">
              Everything you've sent and received through Afriqahome.
            </p>
          </li>
          <li className="bg-white border border-brand-border rounded-xl p-4">
            <p className="font-medium text-gray-900">Agents: your listings and agent profile</p>
            <p className="text-sm text-gray-600 mt-1">
              Any properties you've listed and your public agent profile.
            </p>
          </li>
        </ul>
      </section>

      {/* What we keep */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <ShieldCheck size={20} className="text-brand-primary flex-shrink-0" aria-hidden="true" />
          What we keep, and for how long
        </h2>
        <ul className="space-y-3">
          <li className="bg-white border border-brand-border rounded-xl p-4">
            <p className="text-sm text-gray-600">
              <span className="font-medium text-gray-900">Transaction records</span> — the M-Pesa
              receipt number, amount, date and status — kept for 7 years because the Swedish
              Bookkeeping Act and Kenyan tax law require it. Your account link is removed, the
              paying phone number is cleared, and your email is pseudonymised.
            </p>
          </li>
          <li className="bg-white border border-brand-border rounded-xl p-4">
            <p className="text-sm text-gray-600">
              <span className="font-medium text-gray-900">A de-identified note that your account
              was deleted, and when</span> — a one-way hash of your email plus a deletion
              category, with no email address and no free text — kept up to 24 months to confirm
              we handled your request and help prevent fraud.
            </p>
          </li>
        </ul>
        <p className="text-sm text-gray-600 mt-4">
          Everything else is permanently deleted and cannot be recovered.
        </p>
      </section>

      {/* When it happens */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Clock size={20} className="text-brand-primary flex-shrink-0" aria-hidden="true" />
          When it happens
        </h2>
        <ul className="space-y-3">
          <li className="bg-white border border-brand-border rounded-xl p-4">
            <p className="font-medium text-gray-900">When you confirm</p>
            <p className="text-sm text-gray-600 mt-1">
              We deactivate your account and sign you out everywhere right away.
            </p>
          </li>
          <li className="bg-white border border-brand-border rounded-xl p-4">
            <p className="font-medium text-gray-900">After 30 days</p>
            <p className="text-sm text-gray-600 mt-1">
              Everything above is permanently deleted and can't be recovered.
            </p>
          </li>
        </ul>
      </section>

      {/* How to delete */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          How to delete your account
        </h2>
        <p className="text-gray-600 mb-5">
          To delete your account, sign in and complete the confirmation steps in the app:
        </p>
        <ol className="space-y-4 mb-6">
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-primary text-white text-sm font-semibold flex items-center justify-center">
              1
            </span>
            <div>
              <p className="font-medium text-gray-900">Sign in to Afriqahome</p>
              <p className="text-sm text-gray-600 mt-0.5">
                Use the email or Google account linked to your account.
              </p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-primary text-white text-sm font-semibold flex items-center justify-center">
              2
            </span>
            <div>
              <p className="font-medium text-gray-900">Open Profile → Settings → Delete account</p>
              <p className="text-sm text-gray-600 mt-0.5">
                From your Profile, open Settings, then "Delete account".
              </p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-primary text-white text-sm font-semibold flex items-center justify-center">
              3
            </span>
            <div>
              <p className="font-medium text-gray-900">Confirm to delete permanently</p>
              <p className="text-sm text-gray-600 mt-0.5">
                Follow the on-screen confirmation (tick the box, type DELETE, and
                re-authenticate). This permanently deletes your account.
              </p>
            </div>
          </li>
        </ol>

        <a
          href="/profile"
          className="inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold rounded-full px-6 py-3 transition-colors"
        >
          <Trash2 size={18} aria-hidden="true" />
          Delete my account in the app
        </a>

        <div className="mt-6 bg-gray-50 border border-brand-border rounded-xl p-4">
          <p className="text-sm text-gray-600 flex items-start gap-2">
            <Mail size={16} className="text-gray-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>
              Can't sign in, or prefer us to handle it? Email{' '}
              <a
                href="mailto:info@afriqahome.com"
                className="text-brand-primary hover:underline"
              >
                info@afriqahome.com
              </a>{' '}
              from the address on your account and we'll process your deletion request.
            </span>
          </p>
        </div>
      </section>

      {/* Closing */}
      <p className="text-sm text-gray-500 leading-relaxed border-t border-brand-border pt-6">
        Questions about your data, or want to delete it without signing in? Email{' '}
        <a href="mailto:info@afriqahome.com" className="text-brand-primary hover:underline">
          info@afriqahome.com
        </a>
        . For full details, read our{' '}
        <a href="/privacy" className="text-brand-primary hover:underline">
          Privacy Policy
        </a>
        .
      </p>
    </>
  );
}
